import { Component, Input } from '@angular/core';
import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import { VerificationPagePreview } from './VerificationPagePreview';
import { ProductGroup } from '@app/core/models/client';
import { VerificationFieldConfig } from '@relaynetwork/rn-node-feed-api-types';

@Component({
  selector: 'rn-verification-preview',
  templateUrl: './verification-preview.component.html',
  styleUrl: './verification-preview.component.scss',
  standalone: true,
})
export class VerificationPreviewComponent {
  verificationWrapperId: string;

  @Input() pswEnabled: ProductGroup['password']['enabled'];
  @Input() pswTCS: ProductGroup['password']['tcs'];
  @Input() showPswTCS: ProductGroup['password']['tc_enabled'];
  @Input() showTCS: boolean;
  @Input() pswTCMandatory: ProductGroup['password']['tc_mandatory'];
  @Input() fields: VerificationFieldConfig[] = [];
  @Input() logoUrl: ProductGroup['branding']['icon_s3_url'];
  @Input() color: ProductGroup['branding']['color'];
  @Input() productGroupName: ProductGroup['name'];
  @Input() showProductGroup: ProductGroup['hide_pg_wire_display_name'];
  @Input() productGroupDescription: ProductGroup['description'];
  @Input() fdic: ProductGroup['fdic'];
  @Input() showFdic: boolean;
  @Input() language?: string;
  @Input() showBanner?: boolean;
  @Input() bannerUrl?: ProductGroup['branding']['banner_s3_url'];
  @Input() bannerAltText?: ProductGroup['branding']['banner_alt_text'];
  @Input() showLogo?: boolean;
  @Input() showTitleBar?: boolean;
  @Input() consentTSCS?: string;
  /**
   * When true, pswTCS/pswTCMandatory/consentTSCS are already gated upstream and
   * passed straight through (PDF-preview screen). Editors leave this false and
   * rely on the legacy showPswTCS/showTCS derivation.
   */
  @Input() preGated: boolean = false;
  /** Renders the post-verification error state. */
  @Input() postVerificationError: boolean = false;

  @Input() verificationBody: string = '';
  @Input() verificationButtonLabel: string = '';
  @Input() verificationHeader: string = '';
  /**
   * Product group es overrides (`i18n.es`), forwarded to the React preview so
   * the DS organism resolves customer-configured strings itself. Only the
   * PDF-preview path binds it; editors leave it undefined.
   */
  @Input() i18nEs?: NonNullable<ProductGroup['i18n']>['es'];

  constructor() {}

  root: Root = null;
  ngOnInit() {
    this.verificationWrapperId = 'verification-page-preview';
  }

  protected getRootDomNode() {
    const node = document.getElementById(this.verificationWrapperId);
    return node;
  }

  ngOnDestroy(): void {
    this.root.unmount();
  }

  ngOnChanges(): any {
    this.render();
  }

  protected render() {
    if (this.root) {
      this.root.render(
        React.createElement(VerificationPagePreview, {
          pswEnabled: this.pswEnabled,
          pswTCS: this.pswTCS,
          showPswTCS: this.showPswTCS,
          fields: this.fields,
          showTCS: this.showTCS,
          pswTCMandatory: this.pswTCMandatory,
          logoUrl: this.logoUrl,
          color: this.color,
          productGroupName: this.productGroupName,
          showProductGroup: this.showProductGroup,
          productGroupDescription: this.productGroupDescription,
          fdicSignage: this.fdic,
          showFdic: this.showFdic,
          // DIS-1726: showBanner was declared as an @Input() above but never
          // forwarded here, so the banner never reached VerificationPagePreview
          // regardless of what any caller bound. bannerUrl/bannerAltText are
          // new inputs added for the same fix.
          showBanner: this.showBanner,
          bannerUrl: this.bannerUrl,
          bannerAltText: this.bannerAltText,
          verificationBody: this.verificationBody,
          verificationButtonLabel: this.verificationButtonLabel,
          verificationHeader: this.verificationHeader,
          consentTSCS: this.consentTSCS,
          preGated: this.preGated,
          postVerificationError: this.postVerificationError,
          language: this.language,
          i18nEs: this.i18nEs,
        }),
      );
    }
  }
  ngAfterViewInit(): void {
    const node = document.getElementById(this.verificationWrapperId);
    this.root = createRoot(node);
    this.render();
  }
}
